import { useEffect } from "react";
import { ImageValidity } from "@binah/web-sdk";
import { Screenshot } from "@/services/core-api-adapter";

/**
 * Use this hook to take screenshots of the video element at an interval.
 * The hook will take a screenshot every 10 seconds if the image validity is valid and the number of screenshots is less than the maximum allowed.
 * The hook will set the screenshots state with the new screenshot data.
 * The hook will also clear the interval when the component is unmounted.
 *
 * @param imageValidity The image validity of the face scan.
 * @param screenshots The current list of screenshots.
 * @param setScreenshots The function to set the screenshots state.
 * @param videoRef The ref of the video element.
 * @param quoteId The quote id of the health check.
 */
export function useTakeScreenshot(
  imageValidity: ImageValidity | null,
  screenshots: Screenshot[],
  setScreenshots: React.Dispatch<React.SetStateAction<Screenshot[]>>,
  videoRef?: React.RefObject<HTMLVideoElement>,
  quoteId?: string | null
) {
  useEffect(() => {
    const MAX_SCREENSHOTS = 5;
    const TIME_INTERVAL = 10000; // 10 seconds

    function takeScreenshot(video: HTMLVideoElement) {
      // Create a new canvas element and set its width and height to the video element's width and height
      const canvas = document.createElement("canvas");
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      // Get the 2D drawing context of the canvas
      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      // Draw the video element onto the canvas at (0, 0) with the same width and height as the canvas
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      const imgData = canvas.toDataURL("image/jpeg");
      const timestamp = Date.now();
      const filename = quoteId
        ? `${quoteId}_${timestamp}.jpg`
        : `screenshot_${timestamp}.jpg`;

      setScreenshots((prev) =>
        prev.length < MAX_SCREENSHOTS
          ? [...prev, { filename, data: imgData }]
          : prev
      );
    }

    let interval: ReturnType<typeof setInterval> | null = null;

    if (
      imageValidity === ImageValidity.VALID &&
      screenshots.length < MAX_SCREENSHOTS
    ) {
      interval = setInterval(() => {
        const videoEl =
          videoRef?.current ||
          (document.getElementById("video") as HTMLVideoElement | null);
        if (videoEl && videoEl.videoWidth && videoEl.videoHeight) {
          takeScreenshot(videoEl);
        }
      }, TIME_INTERVAL);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [imageValidity, screenshots.length, setScreenshots, videoRef]);
}
